/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RfidCardInitialProperties,
  RfidCardProperties,
  RfidCardRequest,
} from '../../interface/rfid-cards.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import device from '../../utils/api/device';

// a thunk to fetch users
export const fetchRfidCards = createAsyncThunk<RfidCardProperties[], RfidCardRequest>(
  'rfid-cards/fetchRfidCards',
  async (data: RfidCardRequest, { dispatch }) => {
    try {
      const response = await device.fetchRfidCards(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// a thunk to refresh RFID cards
export const refreshRfidCards = createAsyncThunk<RfidCardProperties[], RfidCardRequest>(
  'rfid-cards/refreshRfidCards',
  async (data: RfidCardRequest) => {
    const response = await device.fetchRfidCards(data);
    return response.data;
  },
);


// a thunk to fetch devices filters
export const fetchRfidCardsFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('device-transaction/fetchRfidCardsFilters', async (_, { dispatch }) => {
  try {
    const response = await device.fetchListTransactionFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: RfidCardInitialProperties = {
  rfidCards: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: deviceFilter,
  filterListLoading: false,
};

// Create the slice
export const RfidCardsSlice = createSlice({
  name: 'rfid-cards',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateRfidCarFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
      }
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchRfidCards.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRfidCards.fulfilled, (state, action) => {
        state.loading = false;
        state.rfidCards = action?.payload.map((rfid: RfidCardProperties) => ({
          ...rfid,
          createdAt: ConvertUTCtoDate(rfid.createdAt),
        }));
      })
      .addCase(fetchRfidCards.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(refreshRfidCards.fulfilled, (state, action) => {
      state.rfidCards = action?.payload.map((rfid: RfidCardProperties) => ({
        ...rfid,
        createdAt: ConvertUTCtoDate(rfid.createdAt),
      }));
    });
    builder
      .addCase(fetchRfidCardsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchRfidCardsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchRfidCardsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateRfidCarFilters } = RfidCardsSlice.actions;
// Export the reducer
export default RfidCardsSlice.reducer;
